import { useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Drawer, IconButton, InputBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { brand } from '../../themes/howdenTheme';
import {
  AVAILABILITY_LABEL,
  CAPABILITY_AREAS,
  capabilitiesInArea,
} from '../capabilities/capabilities';
import type { DataSphereCapability } from '../capabilities/capabilities';

export type NavLink = {
  to: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
};

const SHORT: Record<string, string> = {
  'roadmap-2026': '2026',
  'roadmap-2027': '2027',
};

const useStyles = makeStyles(theme => ({
  paper: {
    width: 440,
    maxWidth: '100vw',
    backgroundColor: brand.surface,
    display: 'flex',
    flexDirection: 'column',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2, 2, 3),
    backgroundColor: brand.navBg,
    color: brand.navText,
  },
  brandName: {
    color: brand.navText,
    fontWeight: 600,
    fontSize: 18,
  },
  brandSub: {
    color: brand.navText,
    opacity: 0.85,
    fontSize: 13,
  },
  close: { color: brand.navText },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 3),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${brand.border}`,
    backgroundColor: brand.subtle,
    '&:focus-within': { borderColor: brand.primary },
  },
  // The box shows focus, so the input inside it does not need its own ring.
  searchInput: {
    flexGrow: 1,
    fontSize: 15,
    '& input:focus-visible': { outline: 'none' },
  },
  body: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 3, 4),
  },
  section: { marginTop: theme.spacing(3) },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: brand.muted,
    marginBottom: theme.spacing(1),
  },
  quick: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  quickLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${brand.border}`,
    color: brand.ink,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    '&:hover': { borderColor: brand.primary, color: brand.primary },
  },
  quickIcon: { fontSize: 20, color: brand.primary },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    margin: theme.spacing(0, -1),
    color: brand.ink,
    fontSize: 15,
    textDecoration: 'none',
    '&:hover': { backgroundColor: brand.subtle, color: brand.primary },
  },
  planned: { color: brand.muted },
  tag: {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    padding: theme.spacing(0, 0.75),
    border: `1px solid ${brand.muted}`,
    color: brand.muted,
  },
  live: {
    flexShrink: 0,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: brand.accent,
  },
  empty: { color: brand.muted, marginTop: theme.spacing(3) },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  links: NavLink[];
  showServices: boolean;
  showPlanned: boolean;
  hrefFor: (capability: DataSphereCapability) => string;
};

/**
 * The full menu, opened from the burger button on the side rail. Pages at the
 * top, then every service by area with what is live and what is planned, and
 * a search box that filters both.
 */
export const NavDrawer = ({
  open,
  onClose,
  links,
  showServices,
  showPlanned,
  hrefFor,
}: Props) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);

  const areas = useMemo(
    () =>
      CAPABILITY_AREAS.filter(area => area.id !== 'insights')
        .map(area => ({
          area,
          items: capabilitiesInArea(area.id).filter(
            item =>
              (showPlanned || item.availability === 'live') &&
              (matches(item.title) || matches(area.title)),
          ),
        }))
        .filter(({ items }) => items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [q, showPlanned],
  );
  const pageLinks = links.filter(link => matches(link.title));

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      aria-label="Main menu"
    >
      <div className={classes.head}>
        <div>
          <div className={classes.brandName}>DataSphere</div>
          <div className={classes.brandSub}>Howden Global Data Lake</div>
        </div>
        <IconButton
          aria-label="Close menu"
          onClick={onClose}
          className={classes.close}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.search}>
        <SearchIcon style={{ color: brand.muted }} aria-hidden />
        <InputBase
          placeholder="Find a page or service"
          value={query}
          onChange={e => setQuery(e.target.value)}
          className={classes.searchInput}
          inputProps={{ 'aria-label': 'Find a page or service' }}
        />
      </div>
      <nav className={classes.body} aria-label="All pages and services">
        {pageLinks.length > 0 ? (
          <section className={classes.section} style={{ marginTop: 0 }}>
            <div className={classes.sectionTitle}>Go to</div>
            <ul className={classes.quick}>
              {pageLinks.map(({ to, title, icon: Icon }) => (
                <li key={to}>
                  <RouterLink
                    to={to}
                    className={classes.quickLink}
                    onClick={onClose}
                  >
                    <Icon className={classes.quickIcon} />
                    {title}
                  </RouterLink>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {showServices
          ? areas.map(({ area, items }) => (
              <section key={area.id} className={classes.section}>
                <div className={classes.sectionTitle}>{area.title}</div>
                <ul className={classes.list}>
                  {items.map(item => {
                    const planned = item.availability !== 'live';
                    return (
                      <li key={item.id}>
                        <RouterLink
                          to={hrefFor(item)}
                          onClick={onClose}
                          className={`${classes.item} ${
                            planned ? classes.planned : ''
                          }`}
                        >
                          <span>{item.title}</span>
                          {planned ? (
                            <span
                              className={classes.tag}
                              title={AVAILABILITY_LABEL[item.availability]}
                            >
                              {SHORT[item.availability]}
                            </span>
                          ) : (
                            <span
                              className={classes.live}
                              title="Available now"
                            />
                          )}
                        </RouterLink>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          : null}
        {pageLinks.length === 0 && areas.length === 0 ? (
          <Typography className={classes.empty}>
            Nothing matches "{query}".
          </Typography>
        ) : null}
      </nav>
    </Drawer>
  );
};
